import React, { useEffect, useState } from "react";
import AdminUserList from "./AdminUserList";
import AdminCompanyList from "./AdminCompanyList";
import AdminInsertUser from "./AdminInsertUser";
import AdminSuperLogin from "./AdminSuperLogin";
import AdminSystemPrompts from "./AdminSystemPrompts";
import AdminMenuVisibility from "./AdminMenuVisibility";
import AdminMarketingConsent from "./AdminMarketingConsent";
import AdminCompanyAccounts from "./AdminCompanyAccounts";
import AdminClassifyHistory from "./AdminClassifyHistory";
import AdminApiUsage from "./AdminApiUsage";
import AdminCourierFreight from "./AdminCourierFreight";
import AdminFormAssets from "./AdminFormAssets";
import AdminLlmModel from "./AdminLlmModel";
import AdminInquiry from "./AdminInquiry";
import AdminAutomation from "./AdminAutomation";
import AdminSubdivision from "./AdminSubdivision";
import AdminDecisionTreeExplorer from "./AdminDecisionTreeExplorer";
import AdminAnswerKey from "./AdminAnswerKey";
import AdminHsModel from "./AdminHsModel";

// ──────────────────────────────────────────
// AdminPage.jsx — DocuAI 어드민 대시보드
// 레거시 /admin 포팅 (사이드바 + 헤더 + 서브패널)
// ──────────────────────────────────────────

// 메일·알림에서 바로 그 화면으로 오게 한다 — `#admin/inquiry` 처럼 해시 뒤에 메뉴 키를 붙인다.
// 모르는 키는 무시하고 기본 화면을 연다(링크가 낡아도 빈 화면이 되지 않게).
export function menuFromHash(hash, keys) {
  const seg = String(hash || "").replace(/^#admin\/?/, "").split(/[/?]/)[0];
  return keys.includes(seg) ? seg : null;
}

const AdminPage = ({ API_BASE_URL, onLogout, onSuperLoginImpersonate }) => {
  const [activeMenu, setActiveMenu] = useState("companyList");

  const sidebarMenus = [
    {
      group: "사용자 관리",
      items: [
        { key: "companyList", label: "사용 업체 조회" },
        { key: "userList", label: "사용자 조회" },
        { key: "insertUser", label: "회원추가" },
        { key: "marketingConsent", label: "마케팅 동의" },
        { key: "companyAccounts", label: "회사 계정·정보공개" },
        { key: "superLogin", label: "슈퍼 로그인" },
        { key: "classifyHistory", label: "분류 이력" },
        { key: "apiUsage", label: "API 사용량" },
        { key: "courierFreight", label: "특송운임 관리" },
        { key: "formAssets", label: "서식 관리" },
        { key: "inquiry", label: "문의/건의" },
      ],
    },
    {
      group: "시스템 설정",
      items: [
        { key: "menuVisibility", label: "메뉴 권한 관리" },
        { key: "systemPrompts", label: "시스템 프롬프트" },
        { key: "llmModel", label: "AI 모델 설정" },
        { key: "automation", label: "자동화 상태" },
        { key: "decisionTreeExplorer", label: "96류 결정트리" },
        { key: "subdivision", label: "결정표(소호)" },
        { key: "answerKey", label: "결정례 정답지" },
        { key: "hsModel", label: "PROVE 모델 서버" },
      ],
    },
  ];

  const menuKeys = sidebarMenus.flatMap((g) => g.items.map((i) => i.key));
  useEffect(() => {
    const want = menuFromHash(window.location.hash, menuKeys);
    if (want) setActiveMenu(want);
    // 해시만 바꿔 들어오는 경우(이미 어드민에 있을 때)도 따라간다
    const onHash = () => {
      const k = menuFromHash(window.location.hash, menuKeys);
      if (k) setActiveMenu(k);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerMenus = [
    { key: "companyList", label: "사용 업체 조회" },
    { key: "userList", label: "사용자 조회" },
    { key: "insertUser", label: "회원추가" },
    { key: "marketingConsent", label: "마케팅 동의" },
    { key: "companyAccounts", label: "회사·정보공개" },
    { key: "superLogin", label: "슈퍼 로그인" },
    { key: "classifyHistory", label: "분류 이력" },
    { key: "apiUsage", label: "API 사용량" },
    { key: "courierFreight", label: "특송운임" },
    { key: "formAssets", label: "서식" },
    { key: "inquiry", label: "문의/건의" },
    { key: "menuVisibility", label: "메뉴 권한" },
    { key: "systemPrompts", label: "시스템 프롬프트" },
    { key: "llmModel", label: "AI 모델 설정" },
    { key: "automation", label: "자동화 상태" },
    { key: "decisionTreeExplorer", label: "96류 결정트리" },
    { key: "subdivision", label: "결정표" },
    { key: "answerKey", label: "정답지" },
    { key: "hsModel", label: "PROVE 모델 서버" },
  ];

  const renderContent = () => {
    switch (activeMenu) {
      case "companyList":
        return <AdminCompanyList API_BASE_URL={API_BASE_URL} />;
      case "userList":
        return <AdminUserList API_BASE_URL={API_BASE_URL} />;
      case "insertUser":
        return <AdminInsertUser API_BASE_URL={API_BASE_URL} />;
      case "marketingConsent":
        return <AdminMarketingConsent API_BASE_URL={API_BASE_URL} />;
      case "companyAccounts":
        return <AdminCompanyAccounts API_BASE_URL={API_BASE_URL} />;
      case "superLogin":
        return (
          <AdminSuperLogin
            API_BASE_URL={API_BASE_URL}
            onImpersonate={onSuperLoginImpersonate}
          />
        );
      case "classifyHistory":
        return <AdminClassifyHistory API_BASE_URL={API_BASE_URL} />;
      case "apiUsage":
        return <AdminApiUsage API_BASE_URL={API_BASE_URL} />;
      case "courierFreight":
        return <AdminCourierFreight API_BASE_URL={API_BASE_URL} />;
      case "formAssets":
        return <AdminFormAssets API_BASE_URL={API_BASE_URL} />;
      case "inquiry":
        return <AdminInquiry API_BASE_URL={API_BASE_URL} />;
      case "automation":
        return <AdminAutomation API_BASE_URL={API_BASE_URL} />;
      case "decisionTreeExplorer":
        return <AdminDecisionTreeExplorer API_BASE_URL={API_BASE_URL} />;
      case "hsModel":
        return <AdminHsModel API_BASE_URL={API_BASE_URL} />;
      case "subdivision":
        return <AdminSubdivision API_BASE_URL={API_BASE_URL} />;
      case "answerKey":
        return <AdminAnswerKey API_BASE_URL={API_BASE_URL} />;
      case "menuVisibility":
        return <AdminMenuVisibility API_BASE_URL={API_BASE_URL} />;
      case "systemPrompts":
        return <AdminSystemPrompts API_BASE_URL={API_BASE_URL} />;
      case "llmModel":
        return <AdminLlmModel API_BASE_URL={API_BASE_URL} />;
      default:
        return <AdminCompanyList API_BASE_URL={API_BASE_URL} />;
    }
  };

  return (
    <div style={S.page}>
      {/* Header */}
      <header style={S.header}>
        <div style={S.headerLeft}>
          <span style={S.headerTitle}>DocuAI 어드민</span>
          <span style={S.headerDivider}>|</span>
          {headerMenus.map((m) => (
            <button
              key={m.key}
              onClick={() => setActiveMenu(m.key)}
              style={{
                ...S.headerMenuBtn,
                opacity: activeMenu === m.key ? 1 : 0.5,
                fontWeight: activeMenu === m.key ? "600" : "400",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div style={S.headerRight}>
          <button onClick={onLogout} style={S.logoutBtn} title="로그아웃">
            홈
          </button>
        </div>
      </header>

      <div style={S.body}>
        {/* Sidebar */}
        <nav style={S.sidebar}>
          {sidebarMenus.map((group) => (
            <div key={group.group} style={S.sideGroup}>
              <div style={S.sideGroupTitle}>{group.group}</div>
              {group.items.map((item) => (
                <div
                  key={item.key}
                  onClick={() => setActiveMenu(item.key)}
                  style={{
                    ...S.sideItem,
                    fontWeight: activeMenu === item.key ? "700" : "400",
                    background:
                      activeMenu === item.key ? "#f3f4f6" : "transparent",
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
          ))}
        </nav>

        {/* Content */}
        <main style={S.content}>{renderContent()}</main>
      </div>
    </div>
  );
};

const S = {
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    fontFamily: "'Malgun Gothic', '맑은 고딕', 'Pretendard Variable', -apple-system, sans-serif",
  },
  header: {
    height: "56px",
    borderBottom: "1px solid #e5e7eb",
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fff",
    flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "12px" },
  headerTitle: { fontSize: "17px", fontWeight: "700", color: "#1f2937" },
  headerDivider: { color: "#d1d5db" },
  headerMenuBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    color: "#374151",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "opacity 0.15s",
  },
  headerRight: { display: "flex", alignItems: "center", gap: "8px" },
  logoutBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    color: "#6b7280",
    padding: "4px 8px",
  },
  body: { display: "flex", flex: 1, overflow: "hidden" },
  sidebar: {
    width: "200px",
    borderRight: "1px solid #e5e7eb",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    background: "#fafafa",
    overflowY: "auto",
    flexShrink: 0,
  },
  sideGroup: { display: "flex", flexDirection: "column", gap: "2px" },
  sideGroupTitle: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#9ca3af",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  sideItem: {
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#374151",
    borderRadius: "6px",
    transition: "background 0.15s",
  },
  content: { flex: 1, overflowY: "auto", background: "#fff" },
};

export default AdminPage;
